import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  LogOut,
  HelpCircle,
  Shield,
  Bell,
  Star,
} from 'lucide-react';
import { User as UserType } from '../../../features/auth/types/user';

interface UserMenuProps {
  user: UserType | null;
  userMenuOpen: boolean;
  setUserMenuOpen: (open: boolean) => void;
  isDashboardPage: boolean;
  logout: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  user,
  userMenuOpen,
  setUserMenuOpen,
  isDashboardPage,
  logout,
}) => {
  const navigate = useNavigate();

  return (
    <div className="relative" data-user-menu>
      <button
        onClick={() => setUserMenuOpen(!userMenuOpen)}
        className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-500 ease-in-out border ${
          isDashboardPage
            ? 'lg:text-gray-700 lg:hover:bg-gray-100 lg:border-gray-200 lg:hover:border-gray-300 lg:bg-white/80 text-gray-700 hover:text-gray-900 hover:bg-gray-100 border-gray-200 hover:border-gray-300 bg-white/80'
            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 border-gray-200 hover:border-gray-300 bg-white/80'
        }`}
        aria-label="User menu"
      >
        {/* User Avatar */}
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center overflow-hidden">
          <span className="text-white font-semibold text-sm">
            {user?.first_name?.charAt(0)?.toUpperCase() || user?.last_name?.charAt(0)?.toUpperCase() || 'U'}
          </span>
        </div>
        
        {/* User Name */}
        <div className="hidden sm:block text-left">
          <p className={`text-sm font-medium transition-all duration-500 ease-in-out ${isDashboardPage ? 'lg:text-gray-900 text-gray-900' : 'text-gray-900'}`}>
            {user?.first_name || 'User'}
          </p>
          <p className={`text-xs transition-all duration-500 ease-in-out ${isDashboardPage ? 'lg:text-gray-600 text-gray-500' : 'text-gray-500'}`}>
            {user?.type === 'operator' ? user?.operator_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : user?.type.charAt(0)?.toUpperCase() || ''}
          </p>
        </div>
      </button>

      {userMenuOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white text-gray-700 rounded-xl shadow-lg py-2 z-50 border border-gray-200">
          {/* Account & Notifications */}
          <div className="mb-2">
            <button
              onClick={() => {
                navigate('/account');
                setUserMenuOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
            >
              <User className="w-4 h-4" />
              <span>Account</span>
            </button>
            <button
              onClick={() => {
                navigate('/notifications');
                setUserMenuOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
            >
              <Bell className="w-4 h-4" />
              <span>Notifications</span>
            </button>
          </div>
          <div className="border-t border-gray-100 my-1"></div>
          
          {/* Business (only for operators) */}
          {user?.type === 'operator' && (
            <div className="mb-2">
              <button
                onClick={() => {
                  navigate('/reviews');
                  setUserMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
              >
                <Star className="w-4 h-4" />
                <span>Reviews</span>
              </button>
              <button
                onClick={() => {
                  navigate('/verification-required');
                  setUserMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
              >
                <Shield className="w-4 h-4" />
                <span>Verification</span>
              </button>
            </div>
          )}
          {user?.type === 'operator' && (
            <div className="border-t border-gray-100 my-1"></div>
          )}
          
          {/* Support */}
          <div className="mb-2">
            <button
              onClick={() => {
                navigate('/help');
                setUserMenuOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
            >
              <HelpCircle className="w-4 h-4" />
              <span>Help Center</span>
            </button>
          </div>
          <div className="border-t border-gray-100 my-1"></div>
          
          <button
            onClick={() => {
              logout();
              setUserMenuOpen(false);
            }}
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
}; 